import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, Loader } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  usePageTitle('Contact');
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', phone: '', service_interest: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const services = lang === 'es' 
    ? [
        'Declaración de Impuestos Individual', 'Declaración de Impuestos Comercial', 'Autónomo / 1099',
        'Declaración Multi-Estado', 'Planificación Fiscal', 'Representación de Auditoría del IRS',
        'Impuestos Atrasados / Deuda Fiscal', 'Configuración Fiscal de Startup', 
        'No listado / Solicitar Consulta'
      ]
    : [
        'Individual Tax Return', 'Business Tax Return', 'Self-Employed / 1099',
        'Multi-State Filing', 'Tax Planning', 'IRS Audit Representation',
        'Back Taxes / Tax Debt', 'Startup Tax Setup',
        'Not listed / Request Consultation'
      ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/leads`, { ...form, source: 'contact' });
      setSuccess(true);
      setForm({ name: '', email: '', phone: '', service_interest: '', message: '' });
    } catch (err) {
      setError(err.response?.data?.detail || t('somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ paddingTop: '72px' }}>
      {/* Hero */}
      <section className="py-16 px-6" style={{ background: '#0B1F3A' }}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="label-uppercase mb-4" style={{ color: '#D4AF37' }}>
            {lang === 'es' ? 'Contáctenos' : 'Contact Us'}
          </div>
          <h1 className="font-heading text-4xl font-extrabold text-white tracking-tight mb-4">
            {t('letsTalk')}
          </h1>
          <p className="text-blue-200 text-lg">
            {t('contactSub')}
          </p>
        </div>
      </section>

      <section className="py-20 px-6 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <h2 className="font-heading text-2xl font-bold text-[#0B1F3A] mb-6">{t('contactTitle')}</h2>
                <div className="space-y-5">
                  {[
                    { icon: Phone, label: t('callUs'), value: '770-235-6999', href: 'tel:7702356999', desc: t('mondayFriday') + ' 9AM–5PM' },
                    { icon: Mail, label: t('emailUs'), value: 'support@mataxpreparerllc.com', href: 'mailto:support@mataxpreparerllc.com', desc: t('respondWithin24') },
                    { icon: MapPin, label: t('visitUs'), value: '4384 Laughlin Ct NW, Kennesaw, GA 30144', href: null, desc: t('byAppointmentOnly') },
                  ].map(({ icon: Icon, label, value, href, desc }) => (
                    <div key={label} className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: '#0B1F3A10' }}>
                        <Icon size={20} className="text-[#0B1F3A]" />
                      </div>
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8] mb-0.5">{label}</div>
                        {href ? (
                          <a href={href} className="font-semibold text-[#0B1F3A] hover:text-[#2563EB] transition-colors">{value}</a>
                        ) : (
                          <p className="font-semibold text-[#0B1F3A]">{value}</p>
                        )}
                        <p className="text-sm text-[#64748B]">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Business Hours */}
              <div className="p-6 bg-white rounded-xl border border-gray-100">
                <h3 className="font-heading font-semibold text-[#0B1F3A] mb-4 flex items-center gap-2">
                  <Clock size={18} className="text-[#D4AF37]" /> {t('businessHours')}
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-[#64748B]">{t('mondayFriday')}</span><span className="font-semibold text-[#0B1F3A]">9:00 AM – 5:00 PM</span></div>
                  <div className="flex justify-between"><span className="text-[#64748B]">{t('saturday')}</span><span className="font-semibold text-[#0B1F3A]">10:00 AM – 2:00 PM</span></div>
                  <div className="flex justify-between"><span className="text-[#64748B]">{t('sunday')}</span><span className="text-[#94A3B8]">{t('closed')}</span></div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                {success ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-[#10B98120] flex items-center justify-center mx-auto mb-4">
                      <CheckCircle size={32} className="text-[#10B981]" />
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-[#0B1F3A] mb-2">{t('messageReceived')}</h3>
                    <p className="text-[#64748B] mb-6">{t('thankYouReaching')}</p>
                    <button onClick={() => setSuccess(false)} className="btn-primary px-6 py-3">
                      {t('sendAnother')}
                    </button>
                  </div>
                ) : (
                  <>
                    <h3 className="font-heading text-xl font-bold text-[#0B1F3A] mb-6">{t('sendUsMessage')}</h3>
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-semibold text-[#475569] mb-2">{t('fullName')} *</label>
                          <input
                            type="text"
                            value={form.name}
                            onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                            required
                            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#0B1F3A] focus:ring-2 focus:ring-[#0B1F3A]/20 outline-none transition-all duration-200 bg-white text-[#0B1F3A] placeholder-gray-400"
                            placeholder={lang === 'es' ? 'Su nombre' : 'Your name'}
                            data-testid="contact-name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-[#475569] mb-2">{t('email')} *</label>
                          <input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                            required
                            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#0B1F3A] focus:ring-2 focus:ring-[#0B1F3A]/20 outline-none transition-all duration-200 bg-white text-[#0B1F3A] placeholder-gray-400"
                            placeholder={lang === 'es' ? 'correo@ejemplo.com' : 'email@example.com'}
                            data-testid="contact-email"
                          />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-semibold text-[#475569] mb-2">{t('phone')}</label>
                          <input
                            type="tel"
                            value={form.phone}
                            onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#0B1F3A] focus:ring-2 focus:ring-[#0B1F3A]/20 outline-none transition-all duration-200 bg-white text-[#0B1F3A] placeholder-gray-400"
                            placeholder="(770) 123-4567"
                            data-testid="contact-phone"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-[#475569] mb-2">{t('serviceInterest')}</label>
                          <select
                            value={form.service_interest}
                            onChange={(e) => setForm(f => ({ ...f, service_interest: e.target.value }))}
                            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#0B1F3A] focus:ring-2 focus:ring-[#0B1F3A]/20 outline-none transition-all duration-200 bg-white text-[#0B1F3A] cursor-pointer"
                            data-testid="contact-service"
                          >
                            <option value="">{t('selectOption')}</option>
                            {services.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#475569] mb-2">{t('message')} *</label>
                        <textarea
                          rows={5}
                          value={form.message}
                          onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                          required
                          className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#0B1F3A] focus:ring-2 focus:ring-[#0B1F3A]/20 outline-none transition-all duration-200 bg-white text-[#0B1F3A] placeholder-gray-400 resize-none"
                          placeholder={lang === 'es' ? '¿Cómo podemos ayudarle?' : 'How can we help you?'}
                          data-testid="contact-message"
                        />
                      </div>
                      {error && <p className="text-red-500 text-sm bg-red-50 p-3 rounded-lg">{error}</p>}
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ background: '#D4AF37', color: '#0B1F3A' }}
                        data-testid="contact-submit"
                      >
                        {loading ? <><Loader size={18} className="animate-spin" /> {t('sending')}</> : <><Send size={18} /> {t('send')}</>}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
